import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  InputNumber,
  message,
  Spin,
  Card,
  Space,
  Divider,
  Row,
  Col,
  Switch,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import TinyEditor from "../../components/Editor";
import { SEO_CONFIG } from "../../seo-congig";

const { TextArea } = Input;

const EditSeller = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingSeller, setLoadingSeller] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState("");
  const [existingBanner, setExistingBanner] = useState(null);
  const [existingIcon, setExistingIcon] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const sellerTypeOptions = [
    { value: "Manufacturer", label: "تولید کننده" },
    { value: "consumer", label: "مصرف کننده" },
  ];

  useEffect(() => {
    const fetchSeller = async () => {
      setLoadingSeller(true);
      try {
        const response = await api.get(`/panel/seller/${id}`);
        const data = response?.data?.data;

        form.setFieldsValue({
          name: data?.name,
          slug: data?.slug,
          type: data?.type?.toString(),
          number: data?.number,
          seo_title: data?.seo_title,
          seo_description: data?.seo_description,
          canonical: data?.canonical,
          follow: data?.follow,
          index: data?.index,
          user_id: data?.user?.id,
          price_inquiry: data?.price_inquiry,
        });

        setEditorContent(data.body);
        setExistingBanner(data.image);

        // Fetch all users for selection and include the previous user
        const usersResponse = await api.get(`/panel/users?search=`);
        const users = usersResponse.data.data.map((user) => ({
          value: user.id,
          label: `${user.name} `,
        }));

        // Ensure the previous user is included in the dropdown
        if (data?.user && !users.some((u) => u.value === data.user.id)) {
          users.unshift({
            value: data.user.id,
            label: `${data.user.name} `,
          });
        }

        setUserOptions(users);
      } catch (error) {
        message.error("خطا در دریافت اطلاعات تامین کننده.");
        navigate("/seller");
      } finally {
        setLoadingSeller(false);
      }
    };

    fetchSeller();
  }, [id, form, navigate]);

  const handleSearchUsers = async (value) => {
    setFetchingUsers(true);
    try {
      const response = await api.get(`/panel/users?search=${value}`);
      const users = response.data.data.map((user) => ({
        value: user?.id,
        label: user?.name,
      }));
      setUserOptions(users);
    } catch (error) {
      message.error("خطا در دریافت مشتریان");
    } finally {
      setFetchingUsers(false);
    }
  };

  // Handle user selection
  const handleUserChange = (value) => {
    form.setFieldsValue({ user_id: value });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const appendIfExists = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      formData.append("name", values.name);
      formData.append("body", editorContent);
      formData.append("slug", values.slug);
      formData.append("type", values.type);
      appendIfExists("user_id", values.user_id);
      formData.append("price_inquiry", values.price_inquiry ? "1" : "0");

      // SEO Fields (ensure empty values are sent)
      appendIfExists("seo_title", values.seo_title);
      appendIfExists("seo_description", values.seo_description);
      appendIfExists("canonical", values.canonical);
      formData.append("follow", values.follow ? "1" : "0");
      formData.append("index", values.index ? "1" : "0");

      if (values.banner?.file) {
        formData.append("image", values.banner.file);
      }

      await api.post(`/panel/seller/${id}?_method=PUT`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("تامین کننده با موفقیت به‌روزرسانی شد.");
      navigate("/seller");
    } catch (error) {
      message.error("خطا در به‌روزرسانی تامین کننده.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div>
        <h2 className="mb-10 text-xl">ویرایش تامین کننده</h2>
        <Spin spinning={loading || loadingSeller}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label="عنوان تامین کننده"
              rules={[
                {
                  required: true,
                  message: "لطفاً عنوان را وارد کنید.",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="user_id"
              label="انتخاب رابط تامین کننده با گیرپاژ"
              // rules={[
              //   { required: true, message: "لطفاً یک تامین کننده را انتخاب کنید." },
              // ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="جستجوی تامین کننده"
                filterOption={false}
                onSearch={handleSearchUsers}
                onChange={handleUserChange}
                notFoundContent={
                  fetchingUsers ? <Spin size="small" /> : "تامین کننده یافت نشد"
                }
                options={userOptions}
              />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="slug"
                  label="پیوند(لینک این تامین کننده در وبسایت)"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً پیوند را وارد کنید.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="type" label="نوع  تامین کننده">
                  <Select placeholder="نوع  تامین کننده را انتخاب کنید">
                    {sellerTypeOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Col span={12}>
              <Form.Item
                name="price_inquiry"
                label="استعلام قیمت توسط کال سنتر"
                valuePropName="checked"
              >
                <Switch defaultChecked="true" />
              </Form.Item>
            </Col>
            <Form.Item
              label="محتوا"
              // required
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!editorContent) {
                      return Promise.reject("لطفاً محتوا را وارد کنید.");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TinyEditor
                content={editorContent}
                onEditorChange={handleEditorChange}
                model={"Brand"}
                height={300}
              />
            </Form.Item>

            <div className="border p-4 rounded-lg mb-4">
              <h3 className="text-xl text-blue-500 text-center mb-4">رسانه</h3>

              <Form.Item
                name="banner"
                label="تصویر بنر"
                extra={
                  existingBanner && (
                    <img
                      className="mt-4"
                      src={existingBanner}
                      alt="بنر موجود"
                      style={{ maxWidth: "200px" }}
                    />
                  )
                }
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    {existingBanner ? "تغییر تصویر" : "انتخاب تصویر"}
                  </Button>
                </Upload>
              </Form.Item>
            </div>

            <Divider>بخش سئو</Divider>
            <Form.Item
              name="seo_title"
              label="عنوان سئو"
              help={SEO_CONFIG.title.helpText}
            >
              <Input maxLength={SEO_CONFIG.title.maxLength} showCount />
            </Form.Item>

            <Form.Item
              name="seo_description"
              label="توضیحات سئو"
              help={SEO_CONFIG.description.helpText}
            >
              <TextArea
                rows={4}
                maxLength={SEO_CONFIG.description.maxLength}
                showCount
              />
            </Form.Item>
            <Form.Item name="canonical" label="URL Canonical">
              <Input />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="follow" label="Follow" valuePropName="checked">
                  <Switch defaultChecked="true" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="index" label="Index" valuePropName="checked">
                  <Switch defaultChecked="true" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  ذخیره تغییرات
                </Button>
                <Button
                  onClick={() => navigate("/seller")}
                  style={{ marginRight: "8px" }}
                >
                  انصراف
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default EditSeller;
